/**
 * CategoryChartsGrid - Charts grouped by category + summary table.
 */

import React, { useMemo } from 'react';
import {
    CategorySpending,
    EXPENSE_CATEGORIES,
    CURRENCY_RATES_TO_TRY,
} from '../../../services/documentApi';
import CategoryChart from './CategoryChart';

interface CategoryChartsGridProps {
    categories: CategorySpending[];
    loading: boolean;
}

const CURRENCY_COLORS: Record<string, string> = {
    TRY: '#3b82f6',
    EUR: '#10b981',
    CHF: '#ef4444',
    USD: '#f59e0b',
    GBP: '#8b5cf6',
};

interface GroupedCategory {
    name: string;
    series: CategorySpending[];
    tryEquivalent: number;
}

const CategoryChartsGrid: React.FC<CategoryChartsGridProps> = ({ categories, loading }) => {
    // Group by category name, merge currencies
    const grouped = useMemo((): GroupedCategory[] => {
        const map = new Map<string, CategorySpending[]>();
        for (const cs of categories) {
            const existing = map.get(cs.category) || [];
            existing.push(cs);
            map.set(cs.category, existing);
        }

        const result: GroupedCategory[] = [];
        map.forEach((series, name) => {
            const tryEq = series.reduce((sum, s) => {
                const rate = CURRENCY_RATES_TO_TRY[s.currency] || 1;
                return sum + s.category_total * rate;
            }, 0);
            result.push({ name, series, tryEquivalent: tryEq });
        });

        return result.sort((a, b) => b.tryEquivalent - a.tryEquivalent);
    }, [categories]);

    if (loading) {
        return (
            <div className="dashboard__section">
                <h3 className="dashboard__section-title">Spending by Category</h3>
                <div className="dashboard__chart-placeholder">Loading...</div>
            </div>
        );
    }

    if (!categories.length) {
        return (
            <div className="dashboard__section">
                <h3 className="dashboard__section-title">Spending by Category</h3>
                <div className="dashboard__chart-placeholder">No category data for this period</div>
            </div>
        );
    }

    return (
        <div className="dashboard__section">
            <h3 className="dashboard__section-title">Spending by Category</h3>

            {/* Summary Table */}
            <table className="dashboard__category-table">
                <thead>
                    <tr>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>TRY Eq.</th>
                    </tr>
                </thead>
                <tbody>
                    {grouped.map((g) => (
                        <tr key={g.name}>
                            <td>{EXPENSE_CATEGORIES[g.name]?.en || g.name}</td>
                            <td>
                                {g.series.map((s) => (
                                    <span key={s.currency} className="dashboard__table-amount" style={{ color: CURRENCY_COLORS[s.currency] || '#6b7280' }}>
                                        {s.category_total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {s.currency}
                                    </span>
                                ))}
                            </td>
                            <td className="dashboard__table-try">
                                {g.tryEquivalent.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Mini Charts Grid */}
            <div className="dashboard__category-grid">
                {grouped.map((g) => (
                    <CategoryChart
                        key={g.name}
                        categoryName={g.name}
                        series={g.series}
                    />
                ))}
            </div>
        </div>
    );
};

export default CategoryChartsGrid;
